
  import { defineComponent, PropType } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import textMixin from '@/common/mixins/text';
  import { TApiResponse } from '@/common/types/response';
  import { toNumberOrNull } from '@/common/utils/number';

  import {
    ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL,
    formatAnalyticsNotesDisplayNumber,
    formatAnalyticsNotesNumberOrUndefined,
    getAnalyticsNotesExportChangeVerb,
    getAnalyticsNotesTradeChangeVerb,
    hasUndefinedMetricGroupValue,
    isAnalyticsNotesUndefinedNumber,
    resolveAnalyticsNotesTrendOrUndefined,
    TAnalyticsNotesFormattableNumber,
  } from '@/modules/ved/utils/analytics-notes/foreignTradeText';

  import { ANALYTICS_NOTES_COMMON_GETTERS_NAME } from '../../store/analyticsNotesCommon/name';
  import {
    ANALYTICS_NOTES_MONTHLY_ACTIONS_NAME,
    ANALYTICS_NOTES_MONTHLY_GETTERS_NAME,
  } from '../../store/analyticsNotesMonthly/name';
  import {
    TAnalyticsNotesFilter,
    TAnalyticsNotesForeignTradeMonthlyView,
    TAnalyticsNotesForeignTradeTemplateDataMonthly,
  } from '../../types/analytics-notes';
  import { getCostTypeName } from '../../utils/options';
  import { getCumulativeMonthPeriodLabel } from '../../utils/table';
  import AnalyticsNotesForeignTradeTextWrapper from './AnalyticsNotesForeignTradeTextWrapper.vue';

  export default defineComponent({
    name: 'AnalyticsNotesForeignTradeTextMonthly',
    components: {
      AnalyticsNotesForeignTradeTextWrapper,
    },
    mixins: [textMixin],
    props: {
      filter: {
        type: Object as PropType<TAnalyticsNotesFilter>,
        required: true,
      },
    },
    data(): {
      isTemplateDataLoading: boolean;
      isInitLoadedData: boolean;
    } {
      return {
        isTemplateDataLoading: false,
        isInitLoadedData: false,
      };
    },
    computed: {
      ...mapGetters({
        foreignTradeTemplateData: ANALYTICS_NOTES_MONTHLY_GETTERS_NAME.getForeignTradeTemplateData,
        getCountryDeclensions: ANALYTICS_NOTES_COMMON_GETTERS_NAME.getCountryDeclensions,
      }),
      countryNameInstrumental() {
        return this.getCountryDeclensions?.countryInInstrumental;
      },
      viewData(): Nullable<TAnalyticsNotesForeignTradeMonthlyView> {
        const rawData = this.foreignTradeTemplateData;
        if (!rawData) {
          return null;
        }

        return {
          countryName: (rawData.countryName ?? '').trim(),
          endPeriodYear: toNumberOrNull(rawData.endPeriodYear),
          previousPeriodYear: toNumberOrNull(rawData.previousPeriodYear),
          totalTradeValue: toNumberOrNull(rawData.totalTradeValue),
          totalTradeDynamics: toNumberOrNull(rawData.totalTradeDynamics),
          exportValue: toNumberOrNull(rawData.exportValue),
          exportDynamics: toNumberOrNull(rawData.exportDynamics),
          importValue: toNumberOrNull(rawData.importValue),
          importDynamics: toNumberOrNull(rawData.importDynamics),
          balanceValue: toNumberOrNull(rawData.balanceValue),
          withoutTekTotalTradeValue: toNumberOrNull(rawData.withoutTekTotalTradeValue),
          withoutTekTotalTradeDynamics: toNumberOrNull(rawData.withoutTekTotalTradeDynamics),
          withoutTekExportValue: toNumberOrNull(rawData.withoutTekExportValue),
          withoutTekExportDynamics: toNumberOrNull(rawData.withoutTekExportDynamics),
          withoutTekBalanceValue: toNumberOrNull(rawData.withoutTekBalanceValue),
          totalTradeValueCum: toNumberOrNull(rawData.totalTradeValueCum),
          exportValueCum: toNumberOrNull(rawData.exportValueCum),
          importValueCum: toNumberOrNull(rawData.importValueCum),
          totalTradeDynamicsCum: toNumberOrNull(rawData.totalTradeDynamicsCum),
          exportDynamicsCum: toNumberOrNull(rawData.exportDynamicsCum),
          importDynamicsCum: toNumberOrNull(rawData.importDynamicsCum),
          withoutTekTotalTradeValueCum: toNumberOrNull(rawData.withoutTekTotalTradeValueCum),
          withoutTekExportValueCum: toNumberOrNull(rawData.withoutTekExportValueCum),
          withoutTekBalanceValueCum: toNumberOrNull(rawData.withoutTekBalanceValueCum),
          withoutTekTotalTradeDynamicsCum: toNumberOrNull(rawData.withoutTekTotalTradeDynamicsCum),
          withoutTekExportDynamicsCum: toNumberOrNull(rawData.withoutTekExportDynamicsCum),
        };
      },
      periodLabel(): string {
        const firstPeriodString = String(this.filter?.periods?.[0] ?? '');
        return getCumulativeMonthPeriodLabel(firstPeriodString);
      },
      getCostTypeName() {
        return getCostTypeName(this.filter.costType).toLowerCase();
      },
      totalTradeTrendVerb(): string {
        return getAnalyticsNotesTradeChangeVerb(this.viewData?.totalTradeDynamics);
      },
      importDynamicsTrendVerb(): string {
        return getAnalyticsNotesTradeChangeVerb(this.viewData?.importDynamics);
      },
      exportDynamicsTrendVerb(): string {
        return getAnalyticsNotesExportChangeVerb(this.viewData?.exportDynamics);
      },
      withoutTekBalanceValueTrendVerb(): string {
        return resolveAnalyticsNotesTrendOrUndefined(this.viewData?.balanceValue, (numericValue) => {
          if (numericValue > 0) return 'Положительное';
          if (numericValue < 0) return 'Отрицательное';
          return 'Нулевое';
        });
      },
      withoutTekTotalTradeTrendVerb(): string {
        return this.withoutTekTrendVerb(this.viewData?.withoutTekTotalTradeDynamics);
      },
      withoutTekExportTradeTrendVerb(): string {
        return this.withoutTekTrendVerb(this.viewData?.withoutTekExportDynamics);
      },
      totalTradeValueCumTrendVerb(): string {
        return resolveAnalyticsNotesTrendOrUndefined(this.viewData?.totalTradeDynamicsCum, (numericValue) => {
          if (numericValue > 0) return 'увеличился';
          if (numericValue === 0) return 'не изменился';
          return 'снизился';
        });
      },
      exportValueCumTrendVerb(): string {
        return this.valueCumTrendVerb(this.viewData?.exportDynamicsCum);
      },
      importValueCumTrendVerb(): string {
        return this.valueCumTrendVerb(this.viewData?.importDynamicsCum);
      },
      withoutTekTotalTradeValueCumTrendVerb(): string {
        return this.withoutTekTrendVerb(this.viewData?.withoutTekTotalTradeDynamicsCum);
      },
      withoutTekExportValueCumTrendVerb(): string {
        return this.withoutTekTrendVerb(this.viewData?.withoutTekExportDynamicsCum);
      },
      getAppgPeriod(): string {
        return `по сравнению с аналогичным периодом ${this.formatDisplayNumber(this.viewData?.endPeriodYear)} г.`;
      },
      getPrevPeriodText(): string {
        let prevPeriodYear = this.viewData?.endPeriodYear;
        if (prevPeriodYear) prevPeriodYear = prevPeriodYear - 1;
        return `по сравнению с ${this.formatDisplayNumber(prevPeriodYear)} г.`;
      },
      // TODO: Эти все методы надо переделать в стратегию.
      getMainTotalTradeSummaryText(): string {
        const totalTradeValue = this.viewData?.totalTradeValue;
        const totalTradeDynamics = this.viewData?.totalTradeDynamics;

        const isTotalTradeValueUndefined = isAnalyticsNotesUndefinedNumber(totalTradeValue);
        const isTotalTradeDynamicsUndefined = isAnalyticsNotesUndefinedNumber(totalTradeDynamics);

        if (isTotalTradeValueUndefined && isTotalTradeDynamicsUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        const trendVerb = this.totalTradeTrendVerb;

        if (!isTotalTradeValueUndefined && isTotalTradeDynamicsUndefined) {
          return `${this.formatValue(totalTradeValue)} ${this.getCostTypeName}
          и ${getAnalyticsNotesTradeChangeVerb(totalTradeValue)} ${this.getPrevPeriodText} ${this.appendTrendVerb(
            getAnalyticsNotesTradeChangeVerb(totalTradeValue),
          )}
          ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}%`;
        }

        return `${this.formatValue(totalTradeValue)} ${this.getCostTypeName} и ${trendVerb} ${
          this.getPrevPeriodText
        } ${this.appendTrendVerb(trendVerb)} ${this.formatPercent(totalTradeDynamics)}`;
      },
      getMainExportSummaryText(): string {
        const exportDynamics = this.viewData?.exportDynamics;
        const exportValue = this.viewData?.exportValue;

        const isExportDynamicsUndefined = isAnalyticsNotesUndefinedNumber(exportDynamics);
        const isExportValueUndefined = isAnalyticsNotesUndefinedNumber(exportValue);

        if (isExportDynamicsUndefined && isExportValueUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isExportValueUndefined && isExportDynamicsUndefined) {
          return `${getAnalyticsNotesExportChangeVerb(
            exportValue,
          )} на ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}% (до уровня ${this.formatValue(exportValue)} ${
            this.getCostTypeName
          })`;
        }

        return `${this.exportDynamicsTrendVerb} на ${this.formatPercent(exportDynamics)} (до уровня ${this.formatValue(
          exportValue,
        )} ${this.getCostTypeName})`;
      },
      getMainImportSummaryText(): string {
        const importDynamics = this.viewData?.importDynamics;
        const importValue = this.viewData?.importValue;

        const isImportDynamicsUndefined = isAnalyticsNotesUndefinedNumber(importDynamics);
        const isImportValueUndefined = isAnalyticsNotesUndefinedNumber(importValue);

        if (isImportDynamicsUndefined && isImportValueUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isImportValueUndefined && isImportDynamicsUndefined) {
          return `${getAnalyticsNotesTradeChangeVerb(importValue)} ${this.appendTrendVerb(
            getAnalyticsNotesTradeChangeVerb(importValue),
          )}  ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}%
          (до уровня ${this.formatValue(importValue)} ${this.getCostTypeName})`;
        }

        return `${this.importDynamicsTrendVerb} на ${this.formatPercent(importDynamics)} (до уровня ${this.formatValue(
          importValue,
        )} ${this.getCostTypeName})`;
      },
      getMainWithoutTekTotalTradeSummaryText(): string {
        const withoutTekTotalTradeValue = this.viewData?.withoutTekTotalTradeValue;
        const withoutTekTotalTradeDynamics = this.viewData?.withoutTekTotalTradeDynamics;

        const isWithoutTekTotalTradeValueUndefined = isAnalyticsNotesUndefinedNumber(withoutTekTotalTradeValue);
        const isWithoutTekTotalTradeDynamicsUndefined = isAnalyticsNotesUndefinedNumber(withoutTekTotalTradeDynamics);

        if (isWithoutTekTotalTradeValueUndefined && isWithoutTekTotalTradeDynamicsUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isWithoutTekTotalTradeValueUndefined && isWithoutTekTotalTradeDynamicsUndefined) {
          const trendVerb = this.withoutTekTrendVerb(withoutTekTotalTradeValue);
          return `${this.formatValue(withoutTekTotalTradeValue)} ${
            this.getCostTypeName
          } (${trendVerb} ${this.appendTrendVerb(trendVerb)} ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}%)`;
        }

        return `${this.formatValue(withoutTekTotalTradeValue)} ${this.getCostTypeName} (${
          this.withoutTekTotalTradeTrendVerb
        } на ${this.formatPercent(withoutTekTotalTradeDynamics)})`;
      },
      getMainWithoutTekExportSummaryText(): string {
        const withoutTekExportValue = this.viewData?.withoutTekExportValue;
        const withoutTekExportDynamics = this.viewData?.withoutTekExportDynamics;

        const isWithoutTekExportValueUndefined = isAnalyticsNotesUndefinedNumber(withoutTekExportValue);
        const isWithoutTekExportDynamicsUndefined = isAnalyticsNotesUndefinedNumber(withoutTekExportDynamics);

        if (isWithoutTekExportValueUndefined && isWithoutTekExportDynamicsUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isWithoutTekExportValueUndefined && isWithoutTekExportDynamicsUndefined) {
          const trendVerb = this.withoutTekTrendVerb(withoutTekExportValue);
          return `${this.formatValue(withoutTekExportValue)} ${
            this.getCostTypeName
          } (${trendVerb} ${this.appendTrendVerb(trendVerb)} ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}%)`;
        }

        return `${this.formatValue(withoutTekExportValue)} ${this.getCostTypeName} (${
          this.withoutTekExportTradeTrendVerb
        } на ${this.formatPercent(withoutTekExportDynamics)})`;
      },
      getMainBalanceSummaryText(): string {
        const withoutTekBalanceValue = this.viewData?.withoutTekBalanceValue;
        const balanceValue = this.viewData?.balanceValue;

        const isWithoutTekBalanceValueUndefined = isAnalyticsNotesUndefinedNumber(withoutTekBalanceValue);
        const isBalanceValueUndefined = isAnalyticsNotesUndefinedNumber(balanceValue);

        if (isWithoutTekBalanceValueUndefined && isBalanceValueUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isWithoutTekBalanceValueUndefined && isBalanceValueUndefined) {
          return `${this.formatValue(withoutTekBalanceValue)} ${this.getCostTypeName}; сальдо России в торговле с ${
            this.countryNameInstrumental
          } в ${this.formatDisplayNumber(
            this.viewData?.endPeriodYear,
          )} году составило ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (isWithoutTekBalanceValueUndefined && !isBalanceValueUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}; сальдо России в торговле с ${
            this.countryNameInstrumental
          } в ${this.formatDisplayNumber(this.viewData?.endPeriodYear)} году составило ${this.formatValue(
            balanceValue,
          )} ${this.getCostTypeName}`;
        }

        return `${this.formatValue(withoutTekBalanceValue)} ${this.getCostTypeName} ${
          this.withoutTekBalanceValueTrendVerb
        } сальдо России в торговле с ${this.countryNameInstrumental} в ${this.formatDisplayNumber(
          this.viewData?.endPeriodYear,
        )} году составило ${this.formatValue(balanceValue)} ${this.getCostTypeName}`;
      },
      getCumTotalTradeSummaryText(): string {
        const totalTradeValueCum = this.viewData?.totalTradeValueCum;
        const totalTradeDynamicsCum = this.viewData?.totalTradeDynamicsCum;

        const isTotalTradeValueCumUndefined = isAnalyticsNotesUndefinedNumber(totalTradeValueCum);
        const isTotalTradeDynamicsCumUndefined = isAnalyticsNotesUndefinedNumber(totalTradeDynamicsCum);

        if (isTotalTradeValueCumUndefined && isTotalTradeDynamicsCumUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}.`;
        }

        if (!isTotalTradeValueCumUndefined && isTotalTradeDynamicsCumUndefined) {
          return `составил ${this.formatValue(totalTradeValueCum)} ${
            this.getCostTypeName
          } и ${getAnalyticsNotesTradeChangeVerb(totalTradeValueCum)} ${this.getAppgPeriod} на ${this.formatPercent(
            totalTradeDynamicsCum,
          )}%.`;
        }

        if (isTotalTradeValueCumUndefined && !isTotalTradeDynamicsCumUndefined) {
          return `${this.totalTradeValueCumTrendVerb} ${this.getAppgPeriod} на ${this.formatPercent(
            totalTradeDynamicsCum,
          )} и составил ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}.`;
        }

        return `объем товарооборота ${this.totalTradeValueCumTrendVerb} ${this.getAppgPeriod} на ${this.formatPercent(
          totalTradeDynamicsCum,
        )} и составил ${this.formatValue(totalTradeValueCum)} ${this.getCostTypeName}`;
      },
      getCumWithoutTekTotalTradeSummaryText(): string {
        const withoutTekTotalTradeValueCum = this.viewData?.withoutTekTotalTradeValueCum;
        const withoutTekTotalTradeDynamicsCum = this.viewData?.withoutTekTotalTradeDynamicsCum;

        const isWithoutTekTotalTradeValueCumUndefined = isAnalyticsNotesUndefinedNumber(withoutTekTotalTradeValueCum);
        const isWithoutTekTotalTradeDynamicsCumUndefined = isAnalyticsNotesUndefinedNumber(
          withoutTekTotalTradeDynamicsCum,
        );

        if (isWithoutTekTotalTradeValueCumUndefined && isWithoutTekTotalTradeDynamicsCumUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isWithoutTekTotalTradeValueCumUndefined && isWithoutTekTotalTradeDynamicsCumUndefined) {
          const trendVerb = this.withoutTekTrendVerb(withoutTekTotalTradeValueCum);

          return `${this.formatValue(withoutTekTotalTradeValueCum)} ${
            this.getCostTypeName
          } (${trendVerb} ${this.appendTrendVerb(trendVerb)} ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}%)`;
        }

        return `${this.formatValue(withoutTekTotalTradeValueCum)} ${this.getCostTypeName} (${
          this.withoutTekTotalTradeValueCumTrendVerb
        } на ${this.formatPercent(withoutTekTotalTradeDynamicsCum)})`;
      },
      getCumWithoutTekExportSummaryText(): string {
        const withoutTekExportValueCum = this.viewData?.withoutTekExportValueCum;
        const withoutTekExportDynamicsCum = this.viewData?.withoutTekExportDynamicsCum;

        const isWithoutTekExportValueCumUndefined = isAnalyticsNotesUndefinedNumber(withoutTekExportValueCum);
        const isWithoutTekExportDynamicsCumUndefined = isAnalyticsNotesUndefinedNumber(withoutTekExportDynamicsCum);

        if (isWithoutTekExportValueCumUndefined && isWithoutTekExportDynamicsCumUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isWithoutTekExportValueCumUndefined && isWithoutTekExportDynamicsCumUndefined) {
          const trendVerb = this.withoutTekTrendVerb(withoutTekExportValueCum);
          return `${this.formatValue(withoutTekExportValueCum)} ${
            this.getCostTypeName
          } (${trendVerb} ${this.appendTrendVerb(trendVerb)} ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}%)`;
        }

        return `${this.formatValue(withoutTekExportValueCum)} ${this.getCostTypeName} (${
          this.withoutTekExportValueCumTrendVerb
        } на ${this.formatPercent(withoutTekExportDynamicsCum)})`;
      },
      getCumWithoutTekBalanceSummaryText(): string {
        const withoutTekBalanceValueCum = this.viewData?.withoutTekBalanceValueCum;

        if (hasUndefinedMetricGroupValue(withoutTekBalanceValueCum)) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}.`;
        }

        return `${this.formatValue(withoutTekBalanceValueCum)} ${this.getCostTypeName}`;
      },
      getCumExportSummaryText(): string {
        const exportDynamicsCum = this.viewData?.exportDynamicsCum;
        const exportValueCum = this.viewData?.exportValueCum;

        const isExportDynamicsCumUndefined = isAnalyticsNotesUndefinedNumber(exportDynamicsCum);
        const isExportValueCumUndefined = isAnalyticsNotesUndefinedNumber(exportValueCum);

        if (isExportDynamicsCumUndefined && isExportValueCumUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}.`;
        }

        if (!isExportValueCumUndefined && isExportDynamicsCumUndefined) {
          const trendVerb = this.withoutTekTrendVerb(exportValueCum);

          return `составил ${this.formatValue(exportValueCum)} ${
            this.getCostTypeName
          } (${trendVerb} ${this.appendTrendVerb(trendVerb)} ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}%)`;
        }

        return `${this.exportValueCumTrendVerb} на ${this.formatPercent(
          exportDynamicsCum,
        )} и составил ${this.formatValue(exportValueCum)} ${this.getCostTypeName}`;
      },
      getCumImportSummaryText(): string {
        const importDynamicsCum = this.viewData?.importDynamicsCum;
        const importValueCum = this.viewData?.importValueCum;

        const isImportDynamicsCumUndefined = isAnalyticsNotesUndefinedNumber(importDynamicsCum);
        const isImportValueCumUndefined = isAnalyticsNotesUndefinedNumber(importValueCum);

        if (isImportDynamicsCumUndefined && isImportValueCumUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isImportValueCumUndefined && isImportDynamicsCumUndefined) {
          const trendVerb = this.withoutTekTrendVerb(importValueCum);

          return `составил ${this.formatValue(importValueCum)} ${
            this.getCostTypeName
          } (${trendVerb} ${this.appendTrendVerb(trendVerb)} ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}%)`;
        }

        return `${this.importValueCumTrendVerb} на ${this.formatPercent(
          importDynamicsCum,
        )} (до уровня ${this.formatValue(importValueCum)} ${this.getCostTypeName})`;
      },
    },
    methods: {
      ...mapActions({
        searchForeignTradeTemplateData: ANALYTICS_NOTES_MONTHLY_ACTIONS_NAME.searchForeignTradeTemplateData,
      }),
      formatDisplayNumber: formatAnalyticsNotesDisplayNumber,
      formatPercent(value: TAnalyticsNotesFormattableNumber): string {
        return formatAnalyticsNotesNumberOrUndefined(
          value,
          (numericValue) => `${this.formatNumber(Math.abs(numericValue), 1)}%`,
        );
      },
      withoutTekTrendVerb(value: TAnalyticsNotesFormattableNumber): string {
        return resolveAnalyticsNotesTrendOrUndefined(value, (numericValue) => {
          if (numericValue > 0) return 'прирост';
          if (numericValue < 0) return 'снижение';
          return 'не изменился';
        });
      },
      appendTrendVerb(value: string): string {
        return value !== 'не изменился' ? 'на' : '';
      },
      valueCumTrendVerb(val: TAnalyticsNotesFormattableNumber): string {
        return resolveAnalyticsNotesTrendOrUndefined(val, (numericValue) => {
          if (numericValue > 0) return 'вырос';
          if (numericValue === 0) return 'не изменился';
          return 'уменьшился';
        });
      },
      formatValue(value: TAnalyticsNotesFormattableNumber): string {
        return formatAnalyticsNotesNumberOrUndefined(value, (numericValue) => this.formatNumber(numericValue, 1));
      },
      async loadTemplateData(filter: TAnalyticsNotesFilter) {
        this.isTemplateDataLoading = true;

        try {
          const response = (await this.searchForeignTradeTemplateData(
            filter,
          )) as TApiResponse<TAnalyticsNotesForeignTradeTemplateDataMonthly>;

          if (response?.success) {
            this.isInitLoadedData = true;
          }
        } finally {
          this.isTemplateDataLoading = false;
        }
      },
    },
    watch: {
      filter: {
        handler(newVal: TAnalyticsNotesFilter) {
          this.loadTemplateData(newVal);
        },
        deep: true,
        immediate: true,
      },
    },
  });
